/**
* Nom : targetList.js
* Description : Ce fichier contient la définition de la page targetList 
*               Cette page liste les cibles
* Date de création : 23 septembre 2024
*/

/* CSS et assets */
// None

/* Bibliothèques et composants */
import { TabRD } from 'package_ui_ihm/dist'
import { Container } from 'package_ui_ihm/dist'
import { AddItem } from 'package_ui_ihm/dist'
import { ListFilter } from 'package_ui_ihm/dist'
import { ItemLink } from 'package_ui_ihm/dist'
import Divider from '@mui/material/Divider';

/* Hook */
import { useState, useEffect } from 'react';
import { useAuth } from 'package_ui_ihm/dist'
import { useApi } from 'package_ui_ihm/dist'
import { useData } from 'package_ui_ihm/dist';
import { useFilter } from 'package_ui_ihm/dist'
import { useScreenWidth } from 'package_ui_ihm/dist'


/**
* TargetList : affiche la page qui liste les cibles
*              filtrage de la liste possible selon la catégorie, la variété et le type de cible
*              un lien permet d'afficher une page avec un formulaire pour créer une nouvelle cible
*              un click sur la cible permet d'afficher sa page avec ses informations détaillées
*
* Type : page 
*
* Parametres : 
*  None
*/
function TargetList() {

  // Define useState hooks
  const [selectedCat, setSelectedCat] = useState(0);
  const [selectedVar, setSelectedVar] = useState(0);
  const [selectedType, setSelectedType] = useState(0);
  const [prevCat, setPrevCat] = useState(0);
  const [categories, setCategories] = useState([])
  const [varieties, setVarieties] = useState([])
  const [targets, setTargets] = useState([])
  const [filteredVarieties, setFilteredVarieties] = useState(varieties);
  const [filteredTargets, setFilteredTargets] = useState([]);
  const [statusCategory, setStatusCategory] = useState(null)
  const [statusVariety, setStatusVariety] = useState(null)
  const [statusTarget, setStatusTarget] = useState(null)
  const [isDataLoading, setDataLoading] = useState(false)


  // Define useContext hooks
  const { authStatus, authToken } = useAuth();
  const { path } = useApi();
  const { targetTypes } = useData();
  const { targetFilter, setTargetFilter } = useFilter();


  // Define useScreenWidth hooks
  const screenWidth = useScreenWidth();


  // Define functions
  function updateFilteredTargets() {
    // Filter category
    if( Number(selectedCat) > 0 && Number(selectedVar) === 0 && Number(selectedType) === 0)
    {
      setFilteredTargets(targets.filter(target => target.category_id === Number(selectedCat)))
    } 
    // Filter variety
    else if( Number(selectedCat) === 0 && Number(selectedVar) > 0 && Number(selectedType) === 0)
    {
      setFilteredTargets(targets.filter(target => target.variety_id === Number(selectedVar)))
    }  
    // Filter target type
    else if( Number(selectedCat) === 0 && Number(selectedVar) === 0 && Number(selectedType) > 0)
    {
      setFilteredTargets(targets.filter(target => target.target_type_id === Number(selectedType)))
    }  
    // Filter category and variety
    else if( Number(selectedCat) > 0 && Number(selectedVar) > 0 && Number(selectedType) === 0)
    {
      setFilteredTargets(targets.filter(target => target.category_id === Number(selectedCat) && target.variety_id === Number(selectedVar)))
    } 
    // Filter variety and target type
    else if( Number(selectedCat) === 0 && Number(selectedVar) > 0 && Number(selectedType) > 0)
    {
      setFilteredTargets(targets.filter(target => target.variety_id === Number(selectedVar) && target.target_type_id === Number(selectedType)))
    }  
    // Filter category and target type
    else if( Number(selectedCat) > 0 && Number(selectedVar) === 0 && Number(selectedType) > 0)
    {
      setFilteredTargets(targets.filter(target => target.category_id === Number(selectedCat) && target.target_type_id === Number(selectedType)))
    } 
    // Filter category and variety and target type
    else if( Number(selectedCat) > 0 && Number(selectedVar) > 0 && Number(selectedType) > 0)
    {
      setFilteredTargets(targets.filter(target => target.category_id === Number(selectedCat) && target.variety_id === Number(selectedVar) && target.target_type_id === Number(selectedType)))
    }   
    // No filter
    else
    {
      setFilteredTargets(targets)
    }
  }

  function updateFilteredVarieties() {
    // Check category change to reinit variety
    if(selectedCat !== prevCat)
    {
      setSelectedVar(0);
      setPrevCat(selectedCat);
    }
    // Filtering of varieties
    if( selectedCat > 0 )
    {
      setFilteredVarieties(varieties.filter((variety) => variety.category_id === Number(selectedCat)))
    }  
    else
    {
      setFilteredVarieties(varieties)
    }
  }

  async function retrieveData() {
    // Token
    const token = "Bearer " + authToken
    // Headers
    const headers = {
      accept: 'application/json',
      Authorization: token,
    }
    // Requests in parallel to win time 
    const [resCategory, resVariety, resTarget] = await Promise.all([
        // Categories
        fetch(path + "/api/bo-cary/", {
            method: 'GET',
            credentials: 'include',
            mode: 'cors',
            headers,
        }),
        // Varieties
        fetch(path + "/api/bo-vaty/", {
            method: 'GET',
            credentials: 'include',
            mode: 'cors',
            headers,
        }),
        // Targets
        fetch(path + "/api/bo-taet/", {
            method: 'GET',
            credentials: 'include',
            mode: 'cors',
            headers,
        }),
    ])
    // Responses in parallel to win time
    const [dataCategory, dataVariety, dataTarget] = await Promise.all([
        resCategory.json(),
        resVariety.json(),
        resTarget.json(),
    ])
    // Set variables
    setStatusCategory(resCategory.ok)
    setCategories(dataCategory["results"].sort((a, b) => (a.name > b.name)?1:-1))
    setStatusVariety(resVariety.ok)
    setVarieties(dataVariety["results"].sort((a, b) => (a.name > b.name)?1:-1))
    setStatusTarget(resTarget.ok)
    setTargets(dataTarget["results"])
    // Dataloading
    setDataLoading(false)
  }

  const filterReset = (event) => {
    event.preventDefault();
    setFilteredTargets(targets)
    setSelectedCat(0)
    setSelectedVar(0)
    setSelectedType(0)
  }

  const saveFilters = (event) => {
    // Ne pas utiliser event.preventDefault(); ici car cela bloque la navigation vers la page targetDetails
    var filters = {
      category_id: selectedCat,
      variety_id: selectedVar,
      target_type_id: selectedType
    }
    setTargetFilter(filters)
  }


  // Define useEffect hooks
  useEffect(() => {
    if(authStatus === 200)
    {
      setDataLoading(true)
      if(targetFilter !== null)
      {
        setSelectedCat(targetFilter['category_id'])
        setSelectedVar(targetFilter['variety_id'])
        setSelectedType(targetFilter['target_type_id'])
        setTargetFilter(null)
      }
      retrieveData()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    updateFilteredVarieties();
    updateFilteredTargets();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targets, selectedCat, selectedVar, selectedType])


  // Render component
  return (
    <div>
      <TabRD tabNumber={1}/>
      <div className={(screenWidth > 650) ? 'App' : 'App-ss'}>
        <Container>
          {isDataLoading ? (
            <p>Chargement des données</p>
          ) : (
            (statusCategory === true && statusVariety === true && statusTarget === true) ? (
              <div className={(screenWidth > 650) ? 'grid-1-3' : null}>
              {/*Normal size of screen*/}
                {/*Filtering*/}
                <div className='flex-rows'>
                  <AddItem path='/targets/new'>Ajouter une cible</AddItem>
                  <h3>Filtres</h3>
                  <ListFilter labelText='Catégorie' choices={categories.sort((a, b) => (a.name > b.name)?1:-1)} choiceParam={'name'} selectedItem={selectedCat} setSelectedItem={setSelectedCat} subfilter={false}/>
                  <ListFilter labelText='Variété' choices={filteredVarieties.sort((a, b) => (a.name > b.name)?1:-1)} choiceParam={'name'} selectedItem={selectedVar} setSelectedItem={setSelectedVar} subfilter={true}/>
                  <ListFilter labelText='Type de cible' choices={targetTypes.sort((a, b) => (a.name > b.name)?1:-1)} choiceParam={'name'} selectedItem={selectedType} setSelectedItem={setSelectedType} subfilter={false}/>
                  <div>
                    <button onClick={filterReset} className='button-secondary clickable'>Sans filtres</button>
                  </div>
                </div>
                {(screenWidth > 650) ? null : <Divider/>}
                {/*Targets list*/}
                <div className='scroll-list-rd'>
                  <h2 className='subtitle'>Nombre de cibles : {filteredTargets.length}</h2>
                  {(filteredTargets && filteredTargets.length !== 0) ? (
                    filteredTargets.sort((a, b) => (a.name > b.name)?1:-1).map((target) => (
                      <ItemLink key={target['id']} id={target['id']} path={"/targets/"} onClick={saveFilters}>
                        <h3 className='item-text'>{target['name']}</h3>
                      </ItemLink>
                    ))
                  ) : (
                    <p>Aucune cible actuellement. Veuillez en créer une !</p>
                  )}
                </div>
              </div>
            ) : (
              <h1>Connexion failed</h1>
            )
          )}
        </Container>
      </div>
    </div>
  );
}

export default TargetList;
